import { AxiosResponse } from 'axios';
import { put, retry, takeLatest } from 'redux-saga/effects';
import {
	error as notifyError,
	success as notifySuccess,
} from '@innovyze/stylovyze';
import {
	CheckOverridesRequest,
	CheckOverridesResponse,
	CheckSensorDeletionsResponse,
	CreateValuemappingProfileRequest,
	GetSingleValuemappingProfileResponse,
	GetValuemappingProfilesParams,
	GetValuemappingProfilesResponse,
	SensorsListResponse,
	UpdateValuemappingProfileRequest,
	ValuemappingProfileCheckSensorDeletionsActionPayload,
} from '@Types';
import * as valueMappingManagerActions from '@Actions/valueMapping';
import { PayloadAction } from '@reduxjs/toolkit';
import {
	checkValuemappingProfileOverrides,
	createValuemappingProfile,
	deleteValuemappingProfile,
	getValueMappingProfile,
	getValueMappingProfiles,
	updateValuemappingProfile,
	checkValuemappingProfileSensorDeletions,
} from '@Services/valueMappingProfiles';
import { parseValuemappingProfileResponse } from '@Utils/valuemappingFormHelpers';
import { navigateTo } from '@Actions/navigate.action';
import { VALUEMAPPING_ROUTES } from '@Types/routes';
import { addRouteNamespace } from '@Utils/actions';
import { getSensorsSensors } from '@Services/sensors';
import { MultiselectOptionWithSensorType } from '@Types/multiselect.types';

function* getValueMappingProfilesSaga(
	action: PayloadAction<GetValuemappingProfilesParams>,
) {
	const { getValueMappingProfilesResolved, getValueMappingProfilesRejected } =
		valueMappingManagerActions;
	try {
		const response: AxiosResponse<GetValuemappingProfilesResponse> =
			yield retry(5, 1500, getValueMappingProfiles, action.payload);
		if (response.data) {
			// yield put(notifySuccess('success'));
			yield put(getValueMappingProfilesResolved(response.data));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('Failed to retreive the list of profiles'));
		yield put(getValueMappingProfilesRejected());
	}
}

function* getSingleValueMappingProfilesSaga(action: PayloadAction<string>) {
	const {
		getSingleValueMappingProfileResolved,
		getSingleValueMappingProfileRejected,
	} = valueMappingManagerActions;
	try {
		yield put(valueMappingManagerActions.getValuemappingSensorsList());
		const response: AxiosResponse<GetSingleValuemappingProfileResponse> =
			yield retry(5, 1500, getValueMappingProfile, action.payload);
		if (response.data) {
			// yield put(notifySuccess('success'));
			const profile = parseValuemappingProfileResponse(response.data);
			yield put(getSingleValueMappingProfileResolved(profile));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('Failed to retreive profile details'));
		yield put(getSingleValueMappingProfileRejected());
	}
}

function* updateValueMappingProfileSaga(
	action: PayloadAction<UpdateValuemappingProfileRequest>,
) {
	const {
		updateValueMappingProfileResolved,
		updateValueMappingProfileRejected,
	} = valueMappingManagerActions;
	try {
		const response: AxiosResponse<GetSingleValuemappingProfileResponse> =
			yield retry(
				1,
				1500,
				updateValuemappingProfile,
				action.payload.id,
				action.payload.body,
			);
		if (response.data) {
			yield put(notifySuccess('Profile was updated successfuly'));
			const profile = parseValuemappingProfileResponse(response.data);
			yield put(updateValueMappingProfileResolved(profile));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('Failed to update the profile'));
		yield put(updateValueMappingProfileRejected());
	}
}

function* deleteValueMappingProfilesSaga(action: PayloadAction<string>) {
	const {
		deleteValuemappingProfileResolved,
		deleteValuemappingProfileRejected,
	} = valueMappingManagerActions;
	try {
		const response: AxiosResponse<unknown> = yield retry(
			5,
			1500,
			deleteValuemappingProfile,
			action.payload,
		);
		if (response.data) {
			yield put(notifySuccess('Profile was deleted successfuly'));
			yield put(deleteValuemappingProfileResolved(action.payload));
			yield put(navigateTo(addRouteNamespace(VALUEMAPPING_ROUTES.list)));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('Failed to delete the profile'));
		yield put(deleteValuemappingProfileRejected());
	}
}
function* valueMappingProfileCheckOverridesSaga(
	action: PayloadAction<CheckOverridesRequest>,
) {
	const {
		checkOverridesValueMappingProfilesResolved,
		checkOverridesValueMappingProfilesRejected,
		setValuemappingProfileDurationDialogOpen,
	} = valueMappingManagerActions;
	try {
		const response: AxiosResponse<CheckOverridesResponse> = yield retry(
			5,
			1500,
			checkValuemappingProfileOverrides,
			action.payload.body,
		);

		// ? Removes profile from retrieved list (to not conflict with itself)
		const filtered =
			response.data && response.data.rows.length
				? response.data.rows.filter(el =>
						action.payload.filterProfileIds
							? !action.payload.filterProfileIds.includes(el.id)
							: true,
				  )
				: [];

		if (filtered.length) {
			yield put(checkOverridesValueMappingProfilesResolved(filtered));
		} else if (!filtered.length) {
			yield put(checkOverridesValueMappingProfilesResolved(filtered));
			yield put(setValuemappingProfileDurationDialogOpen('UPDATE'));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('Overrides check failed'));
		yield put(checkOverridesValueMappingProfilesRejected());
	}
}
function* createValueMappingProfilesSaga(
	action: PayloadAction<CreateValuemappingProfileRequest>,
) {
	const {
		createValueMappingProfilesResolved,
		createValueMappingProfilesRejected,
	} = valueMappingManagerActions;
	try {
		const response: AxiosResponse<unknown> = yield retry(
			5,
			1500,
			createValuemappingProfile,
			action.payload,
		);
		if (response.data) {
			yield put(notifySuccess('Profile was successfuly created'));
			yield put(createValueMappingProfilesResolved(response.data));
			yield put(navigateTo(addRouteNamespace(VALUEMAPPING_ROUTES.list)));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('Profile creation failed'));
		yield put(createValueMappingProfilesRejected());
	}
}
function* getValueMappingProfileSensorIdsSaga() {
	const {
		getValuemappingSensorsListResolved,
		getValuemappingSensorsListRejected,
	} = valueMappingManagerActions;
	try {
		const response: AxiosResponse<SensorsListResponse> = yield retry(
			5,
			1500,
			getSensorsSensors,
		);
		if (response.data && response.data.pagination.total) {
			const sensorIdsOptions = response.data.sensors
				.filter(el => !!el.alias || !!el.sensorId || !!el.sensorType)
				.map(
					(el): MultiselectOptionWithSensorType => ({
						key: el.sensorId,
						value: el.alias || el.sensorId, // ? alias or id
						id: el.sensorId,
						sensorType: el.sensorType,
					}),
				);
			// yield put(notifySuccess('success'));
			yield put(getValuemappingSensorsListResolved(sensorIdsOptions));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('List of sensors could not be retrieved'));
		yield put(getValuemappingSensorsListRejected());
	}
}
function* getValueMappingProfileSensorDeletionsSaga(
	action: PayloadAction<ValuemappingProfileCheckSensorDeletionsActionPayload>,
) {
	const {
		checkSensorDeletionsValueMappingProfilesResolved,
		checkSensorDeletionsValueMappingProfilesRejected,
	} = valueMappingManagerActions;
	try {
		const response: AxiosResponse<CheckSensorDeletionsResponse> =
			yield retry(
				5,
				1500,
				checkValuemappingProfileSensorDeletions,
				action.payload.profileId, // ? id
				action.payload.params, // ? body [ids, types]
			);
		if (response.data && Number.isInteger(response.data.count)) {
			yield put(
				checkSensorDeletionsValueMappingProfilesResolved(response.data),
			);
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		console.error(e);
		yield put(notifyError('Count of sensors could not be retrieved'));
		yield put(checkSensorDeletionsValueMappingProfilesRejected());
	}
}

function* watchGetValueMappingProfiles() {
	yield takeLatest(
		valueMappingManagerActions.getValueMappingProfiles,
		getValueMappingProfilesSaga,
	);
}
function* watchGetSingleValueMappingProfile() {
	yield takeLatest(
		valueMappingManagerActions.getSingleValueMappingProfile,
		getSingleValueMappingProfilesSaga,
	);
}
function* watchValueMappingProfileCheckOverrides() {
	yield takeLatest(
		valueMappingManagerActions.checkOverridesValueMappingProfile,
		valueMappingProfileCheckOverridesSaga,
	);
}
function* watchCreateValueMappingProfiles() {
	yield takeLatest(
		valueMappingManagerActions.createValueMappingProfile,
		createValueMappingProfilesSaga,
	);
}
function* watchUpdateValueMappingProfiles() {
	yield takeLatest(
		valueMappingManagerActions.updateValueMappingProfile,
		updateValueMappingProfileSaga,
	);
}
function* watchDeleteValueMappingProfiles() {
	yield takeLatest(
		valueMappingManagerActions.deleteValuemappingProfile,
		deleteValueMappingProfilesSaga,
	);
}
function* watchGetValueMappingSensorIdsProfiles() {
	yield takeLatest(
		valueMappingManagerActions.getValuemappingSensorsList,
		getValueMappingProfileSensorIdsSaga,
	);
}
function* watchGetValueMappingSensorDeletions() {
	yield takeLatest(
		valueMappingManagerActions.checkSensorDeletionsValueMappingProfile,
		getValueMappingProfileSensorDeletionsSaga,
	);
}

export default [
	watchGetValueMappingProfiles(),
	watchGetSingleValueMappingProfile(),
	watchValueMappingProfileCheckOverrides(),
	watchCreateValueMappingProfiles(),
	watchUpdateValueMappingProfiles(),
	watchDeleteValueMappingProfiles(),
	watchGetValueMappingSensorIdsProfiles(),
	watchGetValueMappingSensorDeletions(),
];
